import { useMemo, useState } from 'react';
import type { AdminRegistryApp } from '@/api/console';

export type AppsSortField =
    | 'code'
    | 'name'
    | 'web_url'
    | 'audience'
    | 'role_link_count'
    | 'enabled';
export type AppsSortDir = 'asc' | 'desc';

export type AppsStatusFilterValue = 'enabled' | 'disabled';
export type AppsCatalogFilterValue = 'ok' | 'never' | 'failed';
export type AppsScopeFilterValue = 'all_orgs' | 'selected_orgs';

/**
 * Which toolbar popover is open — only one at a time (see `AppsFilterButton`'s
 * doc). `'code-search'`/`'name-search'` used to gate the table's own
 * per-column search popovers before those moved onto `DataTable`'s native
 * column filters (EAA-1817) — removed since nothing sets them anymore.
 */
export type AppsOpenFilter = 'status' | 'catalog' | 'scope' | null;

interface UseAppsTableArgs {
    apps: AdminRegistryApp[];
}

function sortValue(app: AdminRegistryApp, field: AppsSortField): string | number {
    if (field === 'enabled') return app.enabled ? 1 : 0;
    return app[field];
}

/**
 * EAA-1817: view-model for `/console/apps`' registry table — the frame 45
 * toolbar (search + Trạng thái/Danh mục/Phạm vi facets + "Xoá bộ lọc"),
 * per-column search on Mã/Tên, and per-column sort. One hook (the "hook
 * model prop" convention from the root CLAUDE.md) because the toolbar and
 * the table body (`AppsTable`, now built on the kit's `DataTable`) read
 * slices of the same state — threading it as individual props would blow
 * past the 6-8 prop ceiling. Mã/Tên search and the Trạng thái filter render
 * as `DataTable`'s own native column-filter icons; this hook still owns the
 * state, just not the popover UI anymore.
 *
 * Each of the three facets is single-select with an implicit "Tất cả" (all)
 * option (`null` clears it) — this mirrors `RoleMapToolbar`'s "Phạm vi"
 * selector and matches frame 45, which shows exactly one selected value per
 * facet rather than a multi-check list.
 */
export function useAppsTable({ apps }: UseAppsTableArgs) {
    const [search, setSearch] = useState('');
    const [codeQuery, setCodeQuery] = useState('');
    const [nameQuery, setNameQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<AppsStatusFilterValue | null>(null);
    const [catalogFilter, setCatalogFilter] = useState<AppsCatalogFilterValue | null>(null);
    const [scopeFilter, setScopeFilter] = useState<AppsScopeFilterValue | null>(null);
    const [openFilter, setOpenFilter] = useState<AppsOpenFilter>(null);
    const [sortField, setSortField] = useState<AppsSortField | null>(null);
    const [sortDir, setSortDir] = useState<AppsSortDir>('asc');

    const normalizedSearch = search.trim().toLowerCase();
    const normalizedCode = codeQuery.trim().toLowerCase();
    const normalizedName = nameQuery.trim().toLowerCase();

    const searchedApps = useMemo(() => {
        return apps.filter((app) => {
            if (
                normalizedSearch &&
                !`${app.code} ${app.name}`.toLowerCase().includes(normalizedSearch)
            ) {
                return false;
            }
            if (normalizedCode && !app.code.toLowerCase().includes(normalizedCode)) return false;
            if (normalizedName && !app.name.toLowerCase().includes(normalizedName)) return false;
            return true;
        });
    }, [apps, normalizedSearch, normalizedCode, normalizedName]);

    // EAA-1817: facet counts are computed against the SEARCH-filtered set only
    // — "how many of what you searched for" — rather than a full cross-facet
    // recomputation against the OTHER two active facets too. Simpler, and
    // still an honest number; a facet never counts itself.
    const statusCounts = useMemo(() => {
        const counts = { enabled: 0, disabled: 0 };
        for (const app of searchedApps) counts[app.enabled ? 'enabled' : 'disabled'] += 1;
        return counts;
    }, [searchedApps]);

    const catalogCounts = useMemo(() => {
        const counts = { ok: 0, never: 0, failed: 0 };
        for (const app of searchedApps) counts[app.catalog_last_sync_status] += 1;
        return counts;
    }, [searchedApps]);

    const scopeCounts = useMemo(() => {
        const counts = { all_orgs: 0, selected_orgs: 0 };
        for (const app of searchedApps) counts[app.org_scope_mode] += 1;
        return counts;
    }, [searchedApps]);

    const filteredApps = useMemo(() => {
        return searchedApps.filter((app) => {
            if (statusFilter && (app.enabled ? 'enabled' : 'disabled') !== statusFilter) {
                return false;
            }
            if (catalogFilter && app.catalog_last_sync_status !== catalogFilter) return false;
            if (scopeFilter && app.org_scope_mode !== scopeFilter) return false;
            return true;
        });
    }, [searchedApps, statusFilter, catalogFilter, scopeFilter]);

    const visibleApps = useMemo(() => {
        if (!sortField) return filteredApps;
        const dirMul = sortDir === 'asc' ? 1 : -1;
        return [...filteredApps].sort((a, b) => {
            const av = sortValue(a, sortField);
            const bv = sortValue(b, sortField);
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dirMul;
            return String(av).localeCompare(String(bv), 'vi') * dirMul;
        });
    }, [filteredApps, sortField, sortDir]);

    /**
     * EAA-1817: `DataTable`'s own `onSortChange` contract — it already runs
     * antd's three-state click cycle (unsorted -> ascend -> descend ->
     * unsorted) internally and reports the RESULT as a plain ordering string
     * (`field`, `-field`, or `''`); this just parses that string into state
     * rather than re-deriving the cycle itself (the hand-rolled `toggleSort`
     * this replaced re-implemented that cycle by hand, which only made sense
     * while the table owned its own sort-header buttons).
     */
    function applySortOrdering(ordering: string) {
        if (!ordering) {
            setSortField(null);
            return;
        }
        if (ordering.startsWith('-')) {
            setSortField(ordering.slice(1) as AppsSortField);
            setSortDir('desc');
            return;
        }
        setSortField(ordering as AppsSortField);
        setSortDir('asc');
    }

    const activeFacetCount = [statusFilter, catalogFilter, scopeFilter].filter(Boolean).length;
    const filtersActive =
        activeFacetCount > 0 ||
        normalizedSearch.length > 0 ||
        normalizedCode.length > 0 ||
        normalizedName.length > 0;

    function clearFilters() {
        setSearch('');
        setCodeQuery('');
        setNameQuery('');
        setStatusFilter(null);
        setCatalogFilter(null);
        setScopeFilter(null);
        setOpenFilter(null);
    }

    return {
        search,
        setSearch,
        codeQuery,
        setCodeQuery,
        nameQuery,
        setNameQuery,
        statusFilter,
        setStatusFilter,
        statusCounts,
        catalogFilter,
        setCatalogFilter,
        catalogCounts,
        scopeFilter,
        setScopeFilter,
        scopeCounts,
        openFilter,
        setOpenFilter,
        sortField,
        sortDir,
        applySortOrdering,
        visibleApps,
        totalCount: apps.length,
        activeFacetCount,
        filtersActive,
        clearFilters,
    };
}

export type AppsTableVM = ReturnType<typeof useAppsTable>;
